import { metersBetween } from './geo.js';
import { pathCumulative, pointAtDistance, projectDistance } from './alongPath.js';
import { GLIDE_MS, SNAP_METERS } from './busMotion.js';

// Road-snapping for buses.
//
// The bus feed reports only a [lng,lat] point every ~30s. Interpolating straight
// between two successive points cuts chords across curves — the classic
// bus-cuts-through-the-East-River-going-up-the-FDR artifact. Instead we snap each
// reported point onto the bus's GTFS route shape and glide the marker ALONG that
// polyline, so it follows the road.
//
// Geometry lives in static, lazily-fetched per-route assets (packages/web/public/
// bus-shapes/<routeId>.json); the server only tags each bus with its route id and
// GTFS direction. A route has several shape variants (directions, branches,
// short-turns); we pick the best-fitting one by projecting the bus onto each.
//
// Everything degrades to the caller's straight-line glide: until a route's shape
// loads, and for any bus that sits too far off its shape (detour, GPS drift, a
// route with no static shape), this returns null and the caller falls back.

const BASE = '/bus-shapes';

/** Projection residual (m) above which we don't trust a snap and fall back to the
 * straight-line glide — the bus is genuinely off its shape (detour / GPS drift). */
const OFF_ROUTE_METERS = 40;
/** Residual (m) above which a locked variant is re-evaluated against all variants
 * (the bus has likely flipped direction or started a new trip). Hysteresis: below
 * this we keep the chosen variant so parallel branches don't flicker. */
const RESELECT_METERS = 60;
/** Penalty (m) added to a variant whose GTFS direction disagrees with the bus's,
 * so the matching direction wins ties but a clearly-better variant can still win. */
const DIR_PENALTY_METERS = 30;

/** One route-shape variant, prepped with cumulative distances for along-path math. */
export interface PreppedVariant {
  coords: [number, number][];
  cum: number[];
  directionId: number;
}

export interface RouteShape {
  variants: PreppedVariant[];
}

/** Raw asset shape (packages/web/public/bus-shapes/<routeId>.json). */
interface RawRouteShape {
  routeId: string;
  variants: { shapeId: string; directionId: number; coords: [number, number][] }[];
}

/** Per-bus snap state: the chosen variant plus a glide in along-path DISTANCE
 * space (mirrors the straight-line glide, but the lerped distance maps back onto
 * the polyline so the bus stays on the road). */
interface BusSnap {
  variant: PreppedVariant;
  fromDist: number;
  toDist: number;
  startMs: number;
  /** True when the bus last projected too far off this variant to trust the snap.
   * The variant is kept (for hysteresis when it returns) but no position is drawn. */
  offRoute: boolean;
}

/** One reported bus this frame. */
export interface SnapFeature {
  id: string;
  position: [number, number];
  routeId: string;
  dir?: 0 | 1;
  /** Previous [lng,lat] to seed the first-sight along-path glide. When provided on
   * first sight, the bus glides from the seed's projection onto the chosen variant
   * toward the reported point, so it moves immediately rather than freezing in place
   * until the next poll. Typically the live glide-tracker position for this bus. */
  seedFrom?: [number, number];
}

export interface BusShapeSnapper {
  /**
   * Snapped [lng,lat] per bus id, or null for buses with no usable snap this frame
   * (shape not loaded yet, no static shape, or too far off-route). A fresh
   * `generatedAt` re-targets the glide; other frames just advance it.
   */
  step(features: SnapFeature[], generatedAt: number, nowMs: number): Map<string, [number, number] | null>;
}

/** Scalar lerp with clamped fraction. */
function lerp1d(from: number, to: number, frac: number): number {
  const f = frac <= 0 ? 0 : frac >= 1 ? 1 : frac;
  return from + (to - from) * f;
}

/** The bus's currently-displayed along-path distance for an in-progress glide. */
function glideDistNow(snap: BusSnap, nowMs: number): number {
  return lerp1d(snap.fromDist, snap.toDist, (nowMs - snap.startMs) / GLIDE_MS);
}

/** Project a point onto a variant, returning its along-path distance and the
 * residual (m) between the point and the nearest spot on the polyline. */
export function projectOnVariant(variant: PreppedVariant, point: [number, number]): {
  dist: number;
  residual: number;
} {
  const dist = projectDistance(variant.coords, variant.cum, point);
  const onPath = pointAtDistance(variant.coords, variant.cum, dist);
  return { dist, residual: metersBetween(point, onPath) };
}

/** Pick the variant that best fits the reported point, biased toward the bus's
 * reported GTFS direction. Returns the variant and its (penalized) residual. */
export function selectVariant(
  shape: RouteShape,
  point: [number, number],
  dir: 0 | 1 | undefined,
): { variant: PreppedVariant; dist: number; residual: number } {
  // Track the winner by penalized SCORE, but return its TRUE geometric residual —
  // the direction penalty only decides which variant wins, it must not leak into
  // the off-route/reselect thresholds the caller checks.
  let best: { variant: PreppedVariant; dist: number; residual: number } | null = null;
  let bestScore = Infinity;
  for (const variant of shape.variants) {
    const { dist, residual } = projectOnVariant(variant, point);
    const score = dir != null && variant.directionId !== dir ? residual + DIR_PENALTY_METERS : residual;
    if (score < bestScore) {
      bestScore = score;
      best = { variant, dist, residual };
    }
  }
  // best is non-null: routes always ship at least one variant (prep() drops empty ones to 'missing').
  return best!;
}

export function createBusShapeSnapper(): BusShapeSnapper {
  // Cache of route shapes. 'loading'/'missing' are sentinels so we fetch each
  // route at most once and never re-request a 404.
  const routes = new Map<string, RouteShape | 'loading' | 'missing'>();
  const snaps = new Map<string, BusSnap>();
  let lastGeneratedAt = 0;

  // Manifest gates fetches: we only request shapes for routes known to exist, so
  // routes without static geometry never trigger a failed request.
  let manifest: Set<string> | null = null;
  let manifestState: 'idle' | 'loading' | 'ready' | 'failed' = 'idle';

  function ensureManifest(): void {
    if (manifestState !== 'idle' || typeof fetch === 'undefined') return;
    manifestState = 'loading';
    fetch(`${BASE}/_manifest.json`)
      .then((r) => (r.ok ? (r.json() as Promise<string[]>) : Promise.reject(new Error(String(r.status)))))
      .then((ids) => {
        manifest = new Set(ids);
        manifestState = 'ready';
      })
      .catch(() => {
        manifestState = 'failed'; // shapes unavailable → everyone falls back
      });
  }

  function prep(raw: RawRouteShape): RouteShape {
    return {
      variants: raw.variants
        .filter((v) => v.coords.length >= 2)
        .map((v) => ({
          coords: v.coords,
          cum: pathCumulative(v.coords),
          directionId: v.directionId,
        })),
    };
  }

  /** Return a loaded route shape, or undefined when it isn't (yet) available —
   * kicking off the manifest/asset fetch as a side effect. */
  function ensureRoute(routeId: string): RouteShape | undefined {
    ensureManifest();
    if (manifestState !== 'ready' || !manifest) return undefined;
    const cached = routes.get(routeId);
    if (cached === 'loading') return undefined;
    if (cached === 'missing') return undefined;
    if (cached) return cached;
    if (!manifest.has(routeId)) {
      routes.set(routeId, 'missing');
      return undefined;
    }
    routes.set(routeId, 'loading');
    fetch(`${BASE}/${encodeURIComponent(routeId)}.json`)
      .then((r) => (r.ok ? (r.json() as Promise<RawRouteShape>) : Promise.reject(new Error(String(r.status)))))
      .then((raw) => {
        const shape = prep(raw);
        routes.set(routeId, shape.variants.length > 0 ? shape : 'missing');
      })
      .catch(() => {
        routes.set(routeId, 'missing');
      });
    return undefined;
  }

  /** Compute (or refresh) a bus's snap and return its position, or null to fall
   * back. Re-targets the glide on a fresh poll or first sight; otherwise advances
   * the existing glide along the shape. */
  function snapBus(f: SnapFeature, isFreshPoll: boolean, nowMs: number): [number, number] | null {
    const shape = ensureRoute(f.routeId);
    if (!shape) {
      snaps.delete(f.id);
      return null;
    }

    const existing = snaps.get(f.id);
    // Re-target only on a fresh poll (or first sight); other frames just advance
    // the in-progress glide — selectVariant's O(points) projection never runs per
    // animation frame.
    if (isFreshPoll || !existing) {
      // Keep the locked variant while it still fits (hysteresis stops parallel
      // branches from flickering); otherwise re-select against all variants.
      const onLocked = existing ? projectOnVariant(existing.variant, f.position) : null;
      const sel =
        onLocked && onLocked.residual <= RESELECT_METERS
          ? { variant: existing!.variant, dist: onLocked.dist, residual: onLocked.residual }
          : selectVariant(shape, f.position, f.dir);

      if (sel.residual > OFF_ROUTE_METERS) {
        // Too far off the shape to trust (detour / GPS drift / no real match). Keep
        // any existing variant lock so the bus resumes cleanly when it returns, but
        // mark it off-route so no snapped position is drawn (caller falls back).
        if (!existing) return null;
        snaps.set(f.id, { ...existing, offRoute: true });
        return null;
      }

      // Glide from the currently-displayed distance toward the new target. A large
      // jump teleports rather than sliding the length of the route. When the variant
      // CHANGES, fromDist belongs to a different distance space, so we must teleport:
      // the `existing.variant === sel.variant` identity check forces from=target.
      // (Variants are stable by reference — built once in prep() and cached.)
      let from: number;
      if (existing) {
        // Continuing bus: glide from current along-path position; teleport on variant
        // change or a big jump.
        const cur = glideDistNow(existing, nowMs);
        from =
          existing.variant === sel.variant && Math.abs(sel.dist - cur) <= SNAP_METERS
            ? cur
            : sel.dist;
      } else if (f.seedFrom) {
        // First sight with a seed: project the seed onto the chosen variant and glide
        // from there to the reported point — so the bus moves on the first frame
        // rather than sitting frozen until the next poll.
        const seedDist = projectOnVariant(sel.variant, f.seedFrom).dist;
        from = Math.abs(sel.dist - seedDist) <= SNAP_METERS ? seedDist : sel.dist;
      } else {
        // First sight, no seed → settle at the reported snap position.
        from = sel.dist;
      }
      snaps.set(f.id, {
        variant: sel.variant,
        fromDist: from,
        toDist: sel.dist,
        startMs: nowMs,
        offRoute: false,
      });
    }

    const snap = snaps.get(f.id)!;
    if (snap.offRoute) return null;
    return pointAtDistance(snap.variant.coords, snap.variant.cum, glideDistNow(snap, nowMs));
  }

  return {
    step(features, generatedAt, nowMs) {
      const isFreshPoll = generatedAt !== lastGeneratedAt;
      if (isFreshPoll) lastGeneratedAt = generatedAt;

      const out = new Map<string, [number, number] | null>();
      const seen = new Set<string>();
      for (const f of features) {
        seen.add(f.id);
        out.set(f.id, snapBus(f, isFreshPoll, nowMs));
      }
      // Prune buses that dropped out of the feed.
      for (const id of snaps.keys()) if (!seen.has(id)) snaps.delete(id);
      return out;
    },
  };
}
